import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { playFlipSound } from "@/lib/sound";

const auraClass = (aura) => {
    switch (aura) {
        case "green":
            return "aura-free";
        case "blue":
            return "aura-pro-blue";
        case "purple":
            return "aura-pro-purple";
        case "orange":
            return "aura-premium-orange";
        case "red":
            return "aura-premium-red";
        default:
            return "";
    }
};

const tierLabel = (req) => {
    if (req === "free") return "FREE PICK";
    if (req === "pro") return "PRO PICK";
    if (req === "sports_premium") return "SPORTS PREMIUM";
    return "PICK";
};

const auraColor = (aura) => {
    switch (aura) {
        case "green":
            return "#22c55e";
        case "blue":
            return "#3b82f6";
        case "purple":
            return "#a855f7";
        case "orange":
            return "#f97316";
        case "red":
            return "#ef4444";
        default:
            return "#a855f7";
    }
};

export const PickFlipCard = ({ slot, isLoggedIn }) => {
    const [flipped, setFlipped] = useState(false);
    const aura = auraClass(slot.aura);
    const auraHex = auraColor(slot.aura);
    const locked = !slot.unlocked;

    const onToggle = () => {
        if (locked) return;
        playFlipSound();
        setFlipped((f) => !f);
    };

    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
        }
    };

    // Reset flip if slot data changes
    useEffect(() => {
        setFlipped(false);
    }, [slot.image, slot.unlocked]);

    const labelTier = tierLabel(slot.tier_required);

    return (
        <div className="relative w-full" style={{ perspective: "1400px" }} data-testid="pick-flip-card" data-locked={locked ? "true" : "false"} data-tier={slot.tier_required}>
            <motion.div
                role="button"
                aria-label={`${labelTier} card. ${locked ? "Locked. Subscribe to unlock." : flipped ? "Showing pick. Click to hide." : "Click to reveal pick."}`}
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={onKey}
                data-testid="pick-flip-card-toggle"
                className={`relative w-full h-[300px] sm:h-[340px] rounded-2xl pp-card-bg cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-[#a855f7] ${aura}`}
                style={{ transformStyle: "preserve-3d" }}
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* FRONT */}
                <div
                    className="absolute inset-0 rounded-2xl p-5 flex flex-col justify-between overflow-hidden"
                    style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                >
                    {/* corner glow */}
                    <div
                        className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-40"
                        style={{ background: auraHex }}
                        aria-hidden
                    />
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="font-display text-3xl tracking-wider text-white" data-testid="pick-tier-label">{labelTier}</p>
                            <p className="mt-1 text-xs text-[#8b8ba0]">{slot.label}</p>
                        </div>
                        <RiskBadge risk={slot.risk} />
                    </div>

                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full pp-bg-2 flex items-center justify-center border border-[#242436] mb-3">
                            <Sparkles className="w-7 h-7" style={{ color: auraHex }} />
                        </div>
                        <p className="font-display text-xl tracking-wider text-white">{slot.has_image ? "TAP TO REVEAL" : "PICK COMING SOON"}</p>
                        <p className="mt-1 text-xs text-[#8b8ba0]">{slot.has_image ? "Owner\u2019s daily slip" : "Owner uploads soon"}</p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-[#8b8ba0]">
                        <span className="font-mono uppercase">D&amp;G</span>
                        <span className="font-mono">DAILY</span>
                    </div>
                </div>

                {/* BACK */}
                <div
                    className="absolute inset-0 rounded-2xl p-3 overflow-hidden"
                    style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                >
                    <div className="relative w-full h-full rounded-xl pp-bg-2 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-2">
                            <RiskBadge risk={slot.risk} />
                            {slot.has_image && slot.image && (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button onClick={(e) => e.stopPropagation()} variant="ghost" size="icon" className="text-[#b7b7c6] hover:text-white" data-testid="pick-slip-zoom-button">
                                            <Maximize2 className="w-4 h-4" />
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="pp-bg-0 border-[#242436] text-white max-w-3xl">
                                        <DialogHeader><DialogTitle className="font-display tracking-wide">{labelTier}</DialogTitle></DialogHeader>
                                        <img src={slot.image} alt="Pick slip" className="w-full rounded-lg" data-testid="pick-slip-image-zoomed" />
                                        {slot.title && <p className="mt-2 font-medium text-white">{slot.title}</p>}
                                        {slot.description && <p className="text-sm text-[#b7b7c6]">{slot.description}</p>}
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>
                        <div className="flex-1 flex items-center justify-center p-1">
                            {slot.image ? (
                                <img
                                    src={slot.image}
                                    alt={`${labelTier} slip`}
                                    className="max-w-full max-h-full object-contain rounded-md"
                                    data-testid="pick-slip-image"
                                />
                            ) : (
                                <div className="text-sm text-[#8b8ba0]">No image yet — check back soon.</div>
                            )}
                        </div>
                        {slot.title && <div className="px-3 py-2 text-xs text-[#b7b7c6] border-t border-[#242436]">{slot.title}</div>}
                    </div>
                </div>
            </motion.div>

            {/* LOCKED OVERLAY */}
            {locked && (
                <div
                    className="absolute inset-0 rounded-2xl pp-bg-0 border border-[#242436] flex flex-col items-center justify-center p-6 text-center"
                    style={{ boxShadow: `0 0 0 1px ${auraHex}33, 0 0 30px ${auraHex}55` }}
                    data-testid="pick-card-locked-overlay"
                >
                    <div className="w-14 h-14 rounded-full pp-bg-1 border border-[#242436] flex items-center justify-center mb-3">
                        <Lock className="w-6 h-6" style={{ color: auraHex }} />
                    </div>
                    <p className="font-display text-2xl tracking-wider text-white">{labelTier}</p>
                    <p className="mt-1 text-sm text-[#b7b7c6]">Unlock with {slot.tier_required === "pro" ? "Pro" : "Sports Premium"}</p>
                    <Button asChild className="mt-4 bg-[#6f3fec] hover:brightness-110 text-white" data-testid="pick-card-locked-overlay-cta">
                        <Link to={isLoggedIn ? "/pricing" : "/signup"}>{isLoggedIn ? "Go to Pricing" : "Sign up to unlock"}</Link>
                    </Button>
                </div>
            )}
        </div>
    );
};
